import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";

const formatBucket = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
};

export default function ThreatDetectionTimeline({
  range = "24h",
  clientId = null,
  incidents = null,
  loadingExternal = false,
  errorExternal = null,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildData = (rows) => {
    const bucket = {};
    rows.forEach((r) => {
      const ts =
        r.timeGenerated ||
        r.lastActivityTime ||
        r.lastModifiedTime ||
        r.createdAt ||
        r.firstActivityTime;
      const key = formatBucket(ts);
      if (!key) return;
      bucket[key] = (bucket[key] || 0) + 1;
    });

    return Object.entries(bucket)
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (Array.isArray(incidents)) {
        setLoading(false);
        setError(errorExternal);
        setData(buildData(incidents));
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const params = {};
        if (range) params.range = range;
        if (range.endsWith("d")) {
          const days = parseInt(range.replace("d", ""), 10);
          if (!Number.isNaN(days)) params.days = days;
        }
        const { data } = await apiClient.get(API_CONFIG.ENDPOINTS.INCIDENTS, { params });
        const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (!mounted) return;
        setData(buildData(rows));
      } catch (err) {
        if (mounted) {
          setError(err.response?.data?.message || err.message || "Failed to load incidents");
          setData([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [range, clientId, incidents, errorExternal]);

  return (
    <Box className="threat-timeline">
      <Box sx={{ marginBottom: "16px" }}>
        <Typography variant="h5" className="dashboard__widget-title">
          Threat Detection Timeline
        </Typography>
        <Typography variant="body2" className="dashboard__widget-subtitle">
          Historical trend analysis of security threats
        </Typography>
      </Box>
      <Box className="threat-timeline__chart">
        {(loading || loadingExternal) && <div className="text-muted">Loading incidents…</div>}
        {(error || errorExternal) && <div className="text-muted">Error: {error || errorExternal}</div>}
        {!loading && !error && data.length === 0 && (
          <div className="text-muted">No incident data for selected filters.</div>
        )}
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6BD4F2" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#6BD4F2" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E0" opacity={0.3} />
            <XAxis
              dataKey="date"
              stroke="#CBD5E0"
              style={{ fontSize: "10px", fontWeight: "bold" }}
            />
            <YAxis
              stroke="#CBD5E0"
              style={{ fontSize: "10px", fontWeight: "bold" }}
              domain={[0, "auto"]}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#6BD4F2"
              fillOpacity={1}
              fill="url(#colorValue)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

